import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { useBasePath } from '../../../lib/use-base-path';
import { profileLinksEnabled } from '../../../lib/profile-links-flag';
import { queryKeys } from '../../../lib/query-client';
import { useProfileLinksList } from '../../../lib/use-profile-links';
import { linkAddress, profileLinksSections, totalContactsOf, totalViewsOf } from '../../../lib/profile-links-model';
import { profilePublicUrl } from '../../../lib/profile-link-view';

/**
 * What every entry point into the owner screens shows (design mock "Where the owner finds it": Settings,
 * Share & earn credits, the account menu) — one hook, so the three cannot disagree on whether the feature
 * is on, which link is "yours", or where a tap goes.
 *
 * `enabled` is `symphonyProfileLinks` (client read — it hides; the routes enforce). The main link is
 * absent until Phase 3 answers, and then until the owner has one; every entry point still opens
 * `/profile-links`, which explains either state.
 */
export interface ProfileLinkEntry {
  enabled: boolean;
  /** `symphony.wix.app/me/<handle>`, once there is a main profile. */
  address: string | null;
  /** The full public URL, for Copy. */
  url: string | null;
  /** Views across ALL the account's links (`totalViews`), shown once there is a main profile. */
  views: number | null;
  /** The people who reached the owner across ALL the account's links (`totalContactsOf`), with `views`. */
  contacts: number | null;
  /** The main link exists and is still a DRAFT: Settings shows "Draft · Publish" and opens the editor. */
  draft: boolean;
  /** `edit` opens the main link's editor (the draft case); `share` its QR format. */
  open: (opts?: { share?: boolean; edit?: boolean }) => void;
}

export function useProfileLinkEntry(): ProfileLinkEntry {
  const router = useRouter();
  const basePath = useBasePath();
  const gate = useQuery({ queryKey: queryKeys.profileLinksGate, queryFn: profileLinksEnabled, staleTime: Infinity });
  const enabled = gate.data === true;
  const list = useProfileLinksList(enabled);
  const main = list.data ? profileLinksSections(list.data.links).main : undefined;
  return {
    enabled,
    address: main ? linkAddress(main) : null,
    url: main ? profilePublicUrl(main.handle) : null,
    views: main ? totalViewsOf(list.data) : null,
    contacts: main ? totalContactsOf(list.data?.links) : null,
    draft: main?.status === 'DRAFT',
    open: (opts) => {
      const route =
        main && opts?.edit
          ? `/profile-links/${encodeURIComponent(main.id)}?tab=card`
          : main && opts?.share
          ? `/profile-links/${encodeURIComponent(main.id)}?share=1`
          : '/profile-links';
      void router.push(route, `${basePath}${route}`);
    },
  };
}
